import { Injectable }            from '@angular/core';
import { AbstractBaseService }   from '@app/core/bases/services/abstractBaseService';
import {
	IAdminTenantHttpParams,
	IBaseHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                from '@app/core/interfaces/api.interface';
import { ADMIN_TENANT_METADATA } from '@app/modules/admin-tenant/config/admin-tenant.config';
import {
	Tenant,
	TenantForm,
	TenantInitialization,
	TenantStatusEnum
}                                from '@app/modules/admin-tenant/models/tenant.model';
import {
	TenantDetailPageRoute,
	TenantDetailPageTabRoute,
	TenantFormPageCreateRoute,
	TenantFormPageCreateSubaccountRoute,
	TenantFormPageUpdateRoute,
	TenantListPageRoute,
	TenantRoutesEnum,
	TenantUsagePageRoute
}                                from '@app/modules/admin-tenant/routes/admin-tenant.routes';
import {
	IPropsTenantEntity,
	IPropsUpdateAdminState
}                                from '@app/modules/admin-tenant/store/admin-tenant.actions';
import { adminTenantStore }      from '@app/modules/admin-tenant/store/admin-tenant.store';
import { UsagePeriodTypeEnum }   from '@app/modules/usage/models/common-usage.model';
import {
	AllowedDetailsTabEnum,
	ObservableTypeEnum,
	ProductEnum
}                                from '@core/interfaces/common.interfaces';
import { IActionItem }           from '@core/interfaces/entity.interface';
import { RouteBasePath }         from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                from '@core/interfaces/store.interface';
import { ProductHelperService }  from '@core/services/product-helper.service';
import { AdminTenantApiService } from '@http/admin/admin-tenant-api.service';
import {
	select,
	Store
}                                from '@ngrx/store';
import {
	IChangeStatusTenantCallbackOptions,
	IStatusFlowChangeEvent
}                                from '@shared/components/status-flow/status-flow.model';
import {
	ListOperations,
	ListOperationsIdentifierEnum
}                                from '@shared/list-operations/model/list-operations.model';
import { Observable }            from 'rxjs';
import {
	map,
	switchMap,
	take
}                                from 'rxjs/operators';


@Injectable( { providedIn: 'root' } )
export class AdminTenantService extends AbstractBaseService {

	// TODO workaround to restore from right ListOps store -> needs final solution somehow connected with routes
	currentActiveListOperationId: ListOperationsIdentifierEnum.ACCOUNTS_LIST_PAGE | ListOperationsIdentifierEnum.CONTRACTS_DETAIL_PAGE_TENANTS_TAB_LIST;

	readonly TENANT_STATUS_ENUM = TenantStatusEnum;

	constructor(
		private readonly adminApi: AdminTenantApiService,
		private readonly productHelperService: ProductHelperService,
		protected readonly store$: Store
	) {
		super( store$, adminTenantStore.selectors, adminTenantStore.actions );
		this.logger.trace( 'AdminTenantService constructor' );
	}

	// ACTIONS ###########################################################################
	private changeTenantStatus(
		callbackOptions: IChangeStatusTenantCallbackOptions,
		statusChangeEvent: IStatusFlowChangeEvent<TenantStatusEnum>
	) {
		let dialogConfirmationText;
		let htmlTenantName;

		const tenant: Tenant = callbackOptions?.entity as Tenant;

		switch ( statusChangeEvent?.targetStatus ) {
			case TenantStatusEnum.ACTIVE:
				htmlTenantName = `<span class="font-bold">${ tenant?.name }</span>`;
				dialogConfirmationText = this.translationService.getI18nStringParams(
					true,
					ADMIN_TENANT_METADATA.i18n.basepath,
					'entity.dialogForActive.htmlDescription',
					{ htmlTenantName }
				);
				break;
			case TenantStatusEnum.DISABLED:
				htmlTenantName = `<span class="font-bold">${ tenant?.name }</span>`;
				dialogConfirmationText = this.translationService.getI18nStringParams(
					true,
					ADMIN_TENANT_METADATA.i18n.basepath,
					'entity.dialogForDisabled.htmlDescription',
					{ htmlTenantName }
				);
				break;
			case TenantStatusEnum.DELETED:
				break;
			case TenantStatusEnum.SUSPENDED:
				break;
		}

		this.store$.dispatch(
			adminTenantStore.actions.changeStatus(
				{
					callback: this.changeStatusCallback,
					callbackOptions,
					caller: callbackOptions?.caller,
					statusFlowChangeEvent: statusChangeEvent,
					dialogConfirmationText
				}
			)
		);
	}

	changeStatusCallback(
		service: AdminTenantService,
		callbackOptions: IChangeStatusTenantCallbackOptions
	) {
		service.checkAndUpdateEntityState(
			callbackOptions?.entity as Tenant,
			callbackOptions?.caller
		);
	}

	changeTenantStatusOnDetailPage(
		tenant: Tenant,
		caller: string,
		statusChangeEvent: IStatusFlowChangeEvent<TenantStatusEnum>
	) {
		this.changeTenantStatus(
			{
				entity: tenant,
				caller
			},
			statusChangeEvent
		);
	}

	changeTenantStatusOnEntity(
		tenant: Tenant,
		caller: string,
		statusChangeEvent: IStatusFlowChangeEvent<TenantStatusEnum>
	) {
		this.changeTenantStatus(
			{
				caller,
				entity: tenant
			},
			statusChangeEvent
		);
	}


	private showAccountConfiguration( tenant: Tenant, caller: string ): void {
		this.store$.dispatch(
			adminTenantStore.actions.showAccountConfiguration(
				{ tenant, caller: caller + ' | AdminTenantService:showAccountConfiguration' }
			)
		);
	}

	showAccountConfigurationOnEntity( tenant: Tenant, caller: string ): void {
		this.showAccountConfiguration( tenant, caller );
	}

	showAccountConfigurationDetailPage( tenant: Tenant, caller: string ): void {
		this.showAccountConfiguration( tenant, caller );
	}

	// CHECKS #################################################################################
	checkAndUpdateEntityState( tenant: Tenant, caller?: string ) {}


	// STORE ###########################################################################
	// SELECTORS
	createState$(): Observable<IActionItem> {
		return this.store$.pipe( select<Record<string, IActionItem>, IActionItem>( adminTenantStore.selectors.selectTenantsEventCreateState ) );
	}

	selectTenantsChildren$( parentTenantId: string ): Observable<Tenant[]> {
		return this.store$.pipe( select<Record<string, Tenant[]>, Tenant[]>( adminTenantStore.selectors.selectTenantsChildren( parentTenantId ) ) );
	}


	// UPDATERS
	updateStoreState( updateAdminState: IPropsUpdateAdminState ) {
		this.store$.dispatch( adminTenantStore.actions.updateAdminState( updateAdminState ) );
	}

	updateTenantContractLogsStore( updateTenantContractLogs: IPropsTenantEntity ) {
		this.store$.dispatch( adminTenantStore.actions.updateTenantContractLogs( updateTenantContractLogs ) );
	}


	// NAVIGATION #################################################################################
	navigateToDetailPage( tenantId: string ) {
		if ( tenantId ) {
			const routeDefinition: TenantDetailPageRoute = {
				basePath: RouteBasePath.Tenant,
				path: TenantRoutesEnum.DetailPage,
				params: { tenantId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'AdminTenantService:navigateToDetailPage',
				false,
				true
			);
		}
	}

	navigateToFormCreate() {
		const routeDefinition: TenantFormPageCreateRoute = {
			basePath: RouteBasePath.Tenant,
			path: TenantRoutesEnum.FormPageCreate
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			'AdminTenantService:navigateToFormCreate'
		);
	}

	navigateToFormCreateSubaccount( parentTenantId: string ) {
		const routeDefinition: TenantFormPageCreateSubaccountRoute = {
			basePath: RouteBasePath.Tenant,
			path: TenantRoutesEnum.FormPageCreateSubaccount,
			params: { parentTenantId }
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			'AccountTenantService:navigateToFormCreate'
		);
	}

	navigateToFormUpdate( tenantId: string ) {
		if ( tenantId ) {
			const routeDefinition: TenantFormPageUpdateRoute = {
				basePath: RouteBasePath.Tenant,
				path: TenantRoutesEnum.FormPageUpdate,
				params: { tenantId }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'AdminTenantService:navigateToFormUpdate'
			);
		}
	}

	navigateToListPage( shouldRestoreListOperationData = false, skipRoutesComparisonCheck = false ) {
		if ( shouldRestoreListOperationData ) {
			this.listOperationsService.state.listOperationsSetRestoreFlag(
				this.currentActiveListOperationId,
				shouldRestoreListOperationData,
				'AdminTenantService:navigateToListPage'
			);
		}

		const routeDefinition: TenantListPageRoute = {
			basePath: RouteBasePath.Tenant,
			path: TenantRoutesEnum.ListPage
		};

		this.routerService.navigateToNextRoute(
			routeDefinition,
			'AdminTenantService:navigateToListPage',
			false,
			false,
			skipRoutesComparisonCheck
		);
	}

	navigateToDetailTab( tenantId: string, tab: AllowedDetailsTabEnum ) {
		if ( tenantId ) {
			const routeDefinition: TenantDetailPageTabRoute = {
				basePath: RouteBasePath.Tenant,
				path: TenantRoutesEnum.DetailPageTab,
				params: { tenantId, tab },
				replaceLastStoredRouteWithNew: true
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'AdminTenantService:navigateToDetailPageTab'
			);
		}
	}

	navigateToUsage( product: ProductEnum, tenantId: string ) {
		if ( tenantId && product ) {
			const routeDefinition: TenantUsagePageRoute = {
				basePath: RouteBasePath.Tenant,
				path: TenantRoutesEnum.UsagePage,
				params: {
					product,
					tenantId,
					usagePeriod: this.productHelperService.getProductUsagePeriod(
						product,
						{ type: UsagePeriodTypeEnum.YEAR },
						{ type: UsagePeriodTypeEnum.YEAR_MONTH },	// TODO: figure out actual ELSTER Contract Usage period
						{ type: UsagePeriodTypeEnum.YEAR_MONTH }
					)
				}
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'AdminTenantService:navigateToUsage',
				false,
				false,
				true
			);
		}
	}

	navigateBack( caller = '', shouldRestoreListOperationData = true ) {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			this.currentActiveListOperationId,
			shouldRestoreListOperationData,
			caller
		);

		this.routerService.navigateBack(
			this.routerService.getFallbackRoute( RouteBasePath.Tenant, TenantRoutesEnum.ListPage ),
			caller + ' | AdminTenantService:navigateBack'
		);
	}

	searchWithStoredListOperationsDataFromDetail(
		tenant: Tenant,
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string
	): Observable<RequestCompleteListState<Tenant>> {
		return this.searchWithStoredListOperationsData(
			listOperationsId,
			caller,
			tenant.id,
			{ updateStore: false }
		)
		.pipe(
			map( ( requestCompleteState ) => {
				if ( requestCompleteState.requestState === RequestState.SUCCESS ) {
					const list: Tenant[] = requestCompleteState.list.map( ( item ) => {
						const itemTmp: Tenant = { ...item };
						itemTmp.showPlainEntity = true;
						return itemTmp;
					} );
					requestCompleteState.list = list;
				}
				return requestCompleteState;
			} )
		);
	}


	searchParentTenants( tenants: Tenant[] ): Observable<RequestCompleteListState<Tenant>> {
		const tenantIds: string[] = [];
		tenants.forEach( ( tenant ) => {
			if ( tenant.parentTenantId && !tenantIds.includes( tenant.parentTenantId ) ) {
				tenantIds.push( tenant.parentTenantId );
			}
		} );

		return this.listOperationsService.select.listOperationsById(
			this.currentActiveListOperationId,
			'AdminTenantService:searchParentTenants'
		)
		.pipe(
			take( 1 ),
			switchMap( ( listOperations: ListOperations ) => {
				return this.search(
					listOperations as IAdminTenantHttpParams,
					'AdminTenantService:searchParentTenants',
					null,
					null,
					tenantIds
				);
			} ),
			take( 1 ),
			map( ( tenantState: RequestCompleteListState<Tenant> ) => {
				const parentList: Tenant[] = tenantState.list;
				const tenantTmpList: Tenant[] = [];
				tenants.forEach( ( tenant: Tenant ) => {
					const tenantTmp = { ...tenant };
					const parent: Tenant = parentList.find( ( parentTenant: Tenant ) => parentTenant.id === tenant.parentTenantId );
					if ( parent ) {
						tenantTmp.parentTenantName = parent.name;
					}
					tenantTmpList.push( tenantTmp );
				} );

				this.storeSetAll( tenantTmpList, 'AdminTenantService:searchByParentTenants' );
				return { ...tenantState, list: tenantTmpList };
			} )
		);
	}


	// API #################################################################################
	// List Requests
	searchWithStoredListOperationsData(
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string,
		tenantId?: string,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteListState<Tenant>> {

		function search( searchListOperationsData: ListOperations, context ): Observable<RequestCompleteListState<Tenant>> {
			return context.search(
				searchListOperationsData as IAdminTenantHttpParams,
				`${ caller } | AdminTenantService:searchWithStoredListOperationsData`,
				storeConfig,
				tenantId
			);
		}

		return this.listOperationsService.helper.requestWithStoredListOperationsData<Tenant, IAdminTenantHttpParams>(
			( [ listOperationsArg ] ) =>
				search( listOperationsArg, this ),
			[ `${ caller } | AdminTenantService:searchWithStoredListOperationsData`, null ],
			0,
			listOperationsId
		);
	}

	searchTenantsByContractIdWithListOperationsData(
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string,
		showPlainEntity?: boolean
	): Observable<RequestCompleteListState<Tenant>> {
		caller = `${ caller } | AdminTenantService:searchTenantsByContractIdWithListOperationsData`;

		return this.listOperationsService.helper.requestWithStoredListOperationsData<Tenant, IAdminTenantHttpParams>(
			( [ listOperationsArg, callerArg, showPlainEntityArg ] ) =>
				this.search(
					{
						...listOperationsArg
					},
					callerArg,
					null,
					null,
					null,
					showPlainEntityArg
				),
			[ caller, showPlainEntity ],
			0,
			listOperationsId
		);
	}

	// TODO before providing storeConfig param a store search selector is needed
	//  for filtering store entries by params
	search(
		params?: IAdminTenantHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig,
		tenantId?: string,
		tenantIds?: string[],
		showPlainEntity?: boolean
	): Observable<RequestCompleteListState<Tenant>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<Tenant>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<Tenant>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | AdminTenantService:search` };
		params = this.generateParams( params ) as IAdminTenantHttpParams;

		const httpParams: IAdminTenantHttpParams = {
			...params,
			archivingContractId: params?.archivingContractId,
			elsterContractId: params?.elsterContractId,
			fiscalContractId: params?.fiscalContractId,
			contractMonth: params?.contractMonth
		};

		if ( tenantIds?.length ) {
			httpParams.tenantIds = tenantIds;
		}
		const req$ = this.adminApi.search( httpParams, null, tenantId );
		const proxyReq$ = this.requestListThroughProxy<Tenant>( req$, storeProcessingConfig );

		proxyReq$.pipe(
			take( 1 ),
			map( ( tenantState: RequestCompleteListState<Tenant> ) => {
				if ( tenantState.requestState === RequestState.SUCCESS && showPlainEntity !== undefined ) {
					let updatedEntities: Tenant[] = [ ...tenantState.list ];
					updatedEntities =
						updatedEntities.map( ( entity: Tenant ) => (
							{ ...entity, showPlainEntity }
						) );
					this.storeUpsertMany( updatedEntities, `${ caller } | AdminTenantService:search->response` );

					tenantState = {
						...tenantState,
						list: updatedEntities
					};
				}
				return tenantState;
			} )
		)
		.subscribe( ( tenantState: RequestCompleteListState<Tenant> ) => {
			if ( tenantState.requestState === RequestState.SUCCESS ) {
				tenantState.list.forEach( tenant => {
					this.checkAndUpdateEntityState( tenant, `${ caller } | AdminTenantService:search->response` );
				} );
			}
		} );

		return proxyReq$;
	}

	list( storeConfig?: IStoreConfig ): Observable<RequestCompleteListState<Tenant>> {
		const storeProcessingConfig = this.createListStoreProcessingConfig<Tenant>(
			storeConfig,
			this.storeList$(),
			StoreActionsEnum.SET_ALL
		);

		const req$ = this.adminApi.list();
		return this.requestListThroughProxy<Tenant>( req$, storeProcessingConfig );
	}

	listTenantsWithInvalidCustomer( params?: IBaseHttpParams, getAllPagesData?: boolean ): Observable<RequestCompleteListState<Tenant>> {
		params = this.generateParams( params );
		const getAllPagesReq$ = ( [ paramsArg ] ) => this.adminApi.listTenantsWithInvalidCustomer( paramsArg );
		return getAllPagesData
					 ? this.requestAllPagesData<Tenant>( getAllPagesReq$, [ params ], 0 )
					 : this.adminApi.listTenantsWithInvalidCustomer( params );
	}

	// Entity Requests
	initialize( tenantInitialization: TenantInitialization, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tenant>> {
		// admin has nothing to store
		storeConfig = {
			getFromStoreFirst: false,
			updateStore: false
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tenant>(
			storeConfig,
			null,
			null
		);

		const req$ = this.adminApi.initialize( tenantInitialization );
		return this.requestEntityThroughProxy<Tenant>( null, req$, storeProcessingConfig );
	}

	read(
		options: {
			caller?: string,
			id: string,
			observableReturnType?: ObservableTypeEnum,
			storeConfig?: IStoreConfig,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<Tenant>> {
		const {
			caller,
			id,
			observableReturnType,
			withFullResponse
		} = options;

		let {
			storeConfig
		} = options;

		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tenant>(
			storeConfig,
			this.storeEntityById$( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this.adminApi.read( id, withFullResponse );
		const proxyReq$ = this.requestEntityThroughProxy<Tenant>( id, req$, storeProcessingConfig, observableReturnType );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tenantState: RequestCompleteEntityState<Tenant> ) => {
			if ( tenantState.requestState === RequestState.SUCCESS ) {
				this.checkAndUpdateEntityState( tenantState.entity, `${ caller } | AdminTenantService:read->response` );
			}
		} );

		return proxyReq$;
	}


	create( tenantForm: TenantForm, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tenant>> {
		storeConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tenant>(
			storeConfig,
			null,
			StoreActionsEnum.ADD_ONE
		);

		const req$ = this.adminApi.create( tenantForm );
		return this.requestEntityThroughProxy<Tenant>( null, req$, storeProcessingConfig );
	}

	update( tenantForm: TenantForm, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tenant>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tenant>(
			storeConfig,
			this.storeEntityById$( tenantForm.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this.adminApi.update( tenantForm );
		return this.requestEntityThroughProxy<Tenant>( tenantForm.id, req$, storeProcessingConfig );
	}

	changeStatus(
		tenant: Tenant,
		status: string,
		storeConfig?: IStoreConfig,
		caller?: string
	): Observable<RequestCompleteEntityState<Tenant>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tenant>(
			storeConfig,
			null,
			StoreActionsEnum.UPSERT_ONE
		);
		const newStatusTenant = { ...tenant };
		newStatusTenant.status = status as TenantStatusEnum;

		const req$ = this.adminApi.changeStatus( newStatusTenant );
		const proxyReq$ = this.requestEntityThroughProxy<Tenant>( null, req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tenantState: RequestCompleteEntityState<Tenant> ) => {
			if ( tenantState.requestState === RequestState.SUCCESS ) {
				this.checkAndUpdateEntityState( tenantState.entity, `${ caller } | AdminTenantService:changeStatus->response` );
			}
		} );

		return proxyReq$;
	}
}
